import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../../environment";

// Assinatura do atendente no chat (04/11/2026, Thiago Castro 9189).
// Com o botão do header ligado, cada mensagem de texto enviada pelo chat sai com o nome do
// atendente em negrito em cima, como o WhatsApp mostra em grupos:
//
//   *Júlio Castro*:
//   Beleza!
//
// O nome é por LOGIN (id_user): começa como o nome do cadastro e, se a pessoa trocar, fica salvo
// só para o login dela (tabela assinatura_atendente; o cadastro não muda). Templates não recebem
// o nome: a Meta não deixa alterar o texto aprovado.
// Liberado por cliente: para abrir para outro, incluir o id aqui E no backend
// (ASSINATURA_CLIENTES_LIBERADOS no api.py).
export const ASSINATURA_GRUPOS = ["9189"];
const MAX_NOME = 60;

export interface EstadoAssinatura {
  liberado: boolean;
  ativo: boolean;
  nome: string;
  nome_padrao: string;
  personalizado: boolean;
}

// Mesma limpeza do backend: sem * _ ~ ` (quebrariam o negrito), espaços colapsados, até 60.
export const limparNomeAssinatura = (v: string): string =>
  (v || "").replace(/[*_~`]/g, "").replace(/\s+/g, " ").trim().slice(0, MAX_NOME).trim();

// Texto que vai para o WhatsApp. Não repete o nome se a mensagem já começa com ele.
export const aplicarAssinatura = (texto: string, estado: EstadoAssinatura | null): string => {
  if (!estado?.liberado || !estado.ativo || !estado.nome) return texto;
  const prefixo = `*${estado.nome}*:`;
  if (texto.trimStart().startsWith(prefixo)) return texto;
  return `${prefixo}\n${texto}`;
};

export function useAssinaturaAtendente() {
  const idGrupo = localStorage.getItem("id_grupo") || "";
  const idUser = localStorage.getItem("id_user") || "";
  const habilitado = ASSINATURA_GRUPOS.includes(idGrupo) && !!idUser;
  const [estado, setEstado] = useState<EstadoAssinatura | null>(null);

  useEffect(() => {
    if (!habilitado) {
      setEstado(null);
      return;
    }
    let vivo = true;
    fetch(`${API_URL}/assinatura_atendente?${new URLSearchParams({ id_user: idUser, id_grupo: idGrupo })}`)
      .then((r) => r.json().catch(() => ({})))
      .then((d) => { if (vivo) setEstado(d?.liberado ? d : null); })
      .catch(() => { if (vivo) setEstado(null); });
    return () => { vivo = false; };
  }, [habilitado, idGrupo, idUser]);

  // Grava no servidor e devolve o estado novo; em falha lança o erro (quem chama mostra).
  const salvar = useCallback(
    async (mudanca: { ativo?: boolean; nome?: string; restaurar?: boolean }) => {
      const r = await fetch(`${API_URL}/assinatura_atendente`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...mudanca, id_user: idUser, id_grupo: idGrupo }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d?.liberado) throw new Error(d?.error || "Não consegui salvar a assinatura.");
      setEstado(d);
      return d as EstadoAssinatura;
    },
    [idGrupo, idUser]
  );

  // Liga/desliga na hora (otimista) e volta atrás se o servidor recusar.
  const alternar = useCallback(async () => {
    if (!estado) return;
    const anterior = estado;
    setEstado({ ...estado, ativo: !estado.ativo });
    try {
      await salvar({ ativo: !anterior.ativo });
    } catch (e) {
      setEstado(anterior);
      throw e;
    }
  }, [estado, salvar]);

  return { estado, salvar, alternar };
}

// Balão de prévia, no jeito do WhatsApp (mensagem enviada).
const Previa = ({ nome, isDark }: { nome: string; isDark: boolean }) => (
  <div
    style={{
      background: isDark ? "#0b141a" : "#efeae2",
      borderRadius: 10,
      padding: "12px 12px 12px 40px",
      display: "flex",
      justifyContent: "flex-end",
    }}
  >
    <div
      style={{
        background: isDark ? "#005c4b" : "#d9fdd3",
        color: isDark ? "#e9edef" : "#111b21",
        borderRadius: "8px 0 8px 8px",
        padding: "6px 9px 4px",
        fontSize: 14,
        lineHeight: 1.35,
        maxWidth: "100%",
        boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
        wordBreak: "break-word",
      }}
    >
      <div>
        <b>{nome || "Seu nome"}</b>:
      </div>
      <div>Olá! Como posso ajudar?</div>
      <div style={{ fontSize: 11, opacity: 0.6, textAlign: "right", marginTop: 2 }}>
        12:26 <i className="ti ti-checks" style={{ color: "#53bdeb", fontSize: 13, verticalAlign: "-2px" }} />
      </div>
    </div>
  </div>
);

export const BotaoAssinatura = ({
  estado,
  salvar,
  alternar,
  isDark,
  onErro,
}: {
  estado: EstadoAssinatura | null;
  salvar: (m: { ativo?: boolean; nome?: string; restaurar?: boolean }) => Promise<EstadoAssinatura>;
  alternar: () => Promise<void>;
  isDark: boolean;
  onErro?: (msg: string) => void;
}) => {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const caixaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  if (!estado?.liberado) return null;

  const ativo = estado.ativo;
  const cor = "var(--brand-primary, #007e94)";
  const limpo = limparNomeAssinatura(rascunho);
  const mudou = limpo !== estado.nome;
  const borda = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)";

  const gravar = async (mudanca: { nome?: string; restaurar?: boolean }) => {
    setSalvando(true);
    setErro(null);
    try {
      const novo = await salvar(mudanca);
      setRascunho(novo.nome);
    } catch (e: any) {
      setErro(e?.message || "Não consegui salvar.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div ref={caixaRef} className="d-inline-flex align-items-center flex-shrink-0" style={{ position: "relative" }}>
      {/* Liga/desliga */}
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center gap-2"
        onClick={() => alternar().catch((e) => onErro?.(e?.message || "Não consegui salvar."))}
        title={
          ativo
            ? `Ligado: suas mensagens saem com "${estado.nome}" em cima. Clique para desligar.`
            : "Mandar o seu nome em negrito em cima de cada mensagem"
        }
        aria-pressed={ativo}
        style={{
          height: 32,
          whiteSpace: "nowrap",
          fontWeight: 600,
          borderRadius: "8px 0 0 8px",
          border: `1px solid ${ativo ? cor : borda}`,
          borderRight: "none",
          background: ativo ? cor : "transparent",
          color: ativo ? "#fff" : isDark ? "#c9d1d9" : "#495057",
          transition: "background .15s, color .15s",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 26, height: 15, borderRadius: 99, position: "relative", flex: "0 0 26px",
            background: ativo ? "rgba(255,255,255,0.35)" : isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)",
            transition: "background .15s",
          }}
        >
          <span
            style={{
              position: "absolute", top: 2, left: ativo ? 13 : 2, width: 11, height: 11, borderRadius: 99,
              background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.25)", transition: "left .15s",
            }}
          />
        </span>
        <i className="ti ti-signature" style={{ fontSize: 16 }} />
        <span className="d-none d-xl-inline">{ativo ? "Assinando" : "Assinar"}</span>
      </button>
      {/* Configurar o nome */}
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center"
        onClick={() => { setRascunho(estado.nome); setErro(null); setAberto(!aberto); }}
        title="Ver e trocar o nome que aparece nas mensagens"
        style={{
          height: 32,
          borderRadius: "0 8px 8px 0",
          border: `1px solid ${ativo ? cor : borda}`,
          background: aberto ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)") : "transparent",
          color: isDark ? "#c9d1d9" : "#495057",
          padding: "0 8px",
        }}
      >
        <i className="ti ti-settings" style={{ fontSize: 16 }} />
      </button>

      {aberto && (
        <div
          role="dialog"
          style={{
            position: "absolute", top: 38, right: 0, width: 310, zIndex: 1060,
            background: isDark ? "#1f2c34" : "#fff", color: isDark ? "#e9edef" : "#1a1a2e",
            border: `1px solid ${borda}`, borderRadius: 12, padding: 14,
            boxShadow: "0 14px 34px rgba(0,0,0,.18)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="ti ti-signature me-1" style={{ color: cor }} />
              Nome nas mensagens
            </div>
            <button type="button" className="btn btn-sm p-0 border-0" style={{ color: "inherit", opacity: 0.6 }} onClick={() => setAberto(false)}>
              <i className="ti ti-x" />
            </button>
          </div>

          <Previa nome={limpo || estado.nome} isDark={isDark} />

          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (limpo && mudou) gravar({ nome: limpo });
            }}
          >
            <label style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.7, display: "block", marginBottom: 3 }}>
              Como o seu nome aparece
            </label>
            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                value={rascunho}
                maxLength={MAX_NOME + 10}
                onChange={(e) => setRascunho(e.target.value)}
                placeholder={estado.nome_padrao || "Seu nome"}
                autoFocus
              />
              <button type="submit" className="btn btn-sm btn-primary" disabled={salvando || !limpo || !mudou}>
                {salvando ? "…" : "Salvar"}
              </button>
            </div>
            <div className="d-flex justify-content-between mt-1" style={{ fontSize: 11, opacity: 0.65 }}>
              <span>Vale só para o seu login ({localStorage.getItem("user_email") || "atual"}).</span>
              <span>{limpo.length}/{MAX_NOME}</span>
            </div>
            {erro && <div className="mt-1" style={{ fontSize: 12, color: "#dc2626" }}>{erro}</div>}
            {estado.personalizado && (
              <button type="button" className="btn btn-sm btn-link px-0 mt-1" disabled={salvando} onClick={() => gravar({ restaurar: true })}>
                Voltar ao nome do cadastro ({estado.nome_padrao})
              </button>
            )}
          </form>

          <div className="mt-2 pt-2" style={{ fontSize: 11.5, opacity: 0.7, borderTop: `1px dashed ${borda}` }}>
            {ativo ? (
              <>
                <i className="ti ti-circle-check me-1" style={{ color: "#16a34a" }} />
                Ligado: toda mensagem que você mandar pelo chat sai assim.
              </>
            ) : (
              <>
                <i className="ti ti-info-circle me-1" />
                Desligado: ligue no botão ao lado para assinar as mensagens.
              </>
            )}{" "}
            Templates saem sem o nome.
          </div>
        </div>
      )}
    </div>
  );
};
